<template>
  <div>
    <v-card class="pa-10">
      <h1 class="mb-8">{{ $t('tslConfigurator.title') }}</h1>

      <Loader v-if="loading" />

      <v-form v-else ref="form" v-model="valid" @submit.prevent="save">
        <v-row>
          <!-- Logo Upload -->
          <v-col cols="12" md="6">
            <label>{{ $t('tslConfigurator.logo') }}</label>
            <v-row>
              <v-col cols="4">
                <div v-if="form.logo || logoPreview" class="d-flex mb-2">
                  <v-avatar rounded class="avatar-responsive">
                    <img :src="getLogoUrl(form.logo)" alt="Logo" />
                  </v-avatar>
                </div>
                <div v-else class="mb-2">
                  <div class="avatar rounded"></div>
                </div>
                <UploadFileButton
                  :label="$t('companiesDetails.changeLogo')"
                  :multiple="false"
                  :showFileList="false"
                  :items="[]"
                  outlined
                  accept="image/*"
                  @input="uploadLogo"
                />
              </v-col>
            </v-row>
          </v-col>

          <!-- Header Text -->
          <v-col cols="12" md="6">
            <label>{{ $t('tslConfigurator.headerText') }}</label>
            <v-textarea
              v-model="form.headerText"
              outlined
              dense
              rows="3"
              :placeholder="$t('tslConfigurator.headerTextPlaceholder')"
              counter="500"
            ></v-textarea>
          </v-col>

          <!-- Bank Account Number -->
          <v-col cols="12" md="6">
            <label>{{ $t('tslConfigurator.bankAccount') }}</label>
            <v-text-field
              v-model="form.bankAccountNumber"
              outlined
              dense
              :placeholder="$t('tslConfigurator.bankAccountPlaceholder')"
              counter="100"
            ></v-text-field>
          </v-col>

          <!-- SWIFT Code for First Account -->
          <v-col cols="12" md="6">
            <label>{{ $t('tslConfigurator.swiftCode') }}</label>
            <v-text-field
              v-model="form.swiftCode"
              outlined
              dense
              :placeholder="$t('tslConfigurator.swiftCodePlaceholder')"
              counter="20"
            ></v-text-field>
          </v-col>

          <!-- Bank Account Number 2 -->
          <v-col cols="12" md="6">
            <label>{{ $t('tslConfigurator.bankAccount2') }}</label>
            <v-text-field
              v-model="form.bankAccount2"
              outlined
              dense
              :placeholder="$t('tslConfigurator.bankAccountPlaceholder')"
              counter="100"
            ></v-text-field>
          </v-col>

          <!-- SWIFT Code for Second Account -->
          <v-col cols="12" md="6">
            <label>{{ $t('tslConfigurator.swiftCode2') }}</label>
            <v-text-field
              v-model="form.swiftCode2"
              outlined
              dense
              :placeholder="$t('tslConfigurator.swiftCodePlaceholder')"
              counter="20"
            ></v-text-field>
          </v-col>

          <!-- Barcode Type -->
          <v-col cols="12" md="6">
            <label>{{ $t('tslConfigurator.barcodeType') }}</label>
            <v-select
              v-model="form.barcodeType"
              :items="barcodeTypes"
              item-text="text"
              item-value="value"
              outlined
              dense
            ></v-select>
          </v-col>

          <!-- Order Conditions (WYSIWYG) -->
          <v-col cols="12">
            <RichTextEditor
              v-model="form.orderConditions"
              :label="$t('tslConfigurator.orderConditions')"
              :placeholder="$t('tslConfigurator.orderConditionsPlaceholder')"
              toolbarType="full"
            />
          </v-col>

          <!-- Information Clause (WYSIWYG) -->
          <v-col cols="12">
            <RichTextEditor
              v-model="form.informationClause"
              :label="$t('tslConfigurator.informationClause')"
              :placeholder="$t('tslConfigurator.informationClausePlaceholder')"
              toolbarType="full"
            />
          </v-col>

          <!-- Action Buttons -->
          <v-col cols="12" class="d-flex justify-end" style="gap: 12px;">
            <v-btn
              color="accent"
              outlined
              large
              :disabled="!valid"
              @click="openPreview"
            >
              <v-icon left>mdi-eye</v-icon>
              {{ $t('tslConfigurator.preview') }}
            </v-btn>
            <v-btn
              color="primary"
              large
              type="submit"
              :loading="saving"
              :disabled="!valid"
            >
              {{ $t('tslConfigurator.save') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card>

    <!-- Preview Dialog -->
    <v-dialog v-model="previewDialog" max-width="1400px" scrollable>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>{{ $t('tslConfigurator.previewTitle') }}</span>
          <v-btn icon @click="previewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pdf-container">
          <v-progress-linear v-if="loadingPreview" indeterminate />
          <div v-else-if="previewPdfUrl" class="pdf-wrapper">
            <object
              :data="previewPdfUrl"
              type="application/pdf"
              width="100%"
              height="800px"
            >
              <p class="text-center pa-4">
                {{ $t('tslConfigurator.pdfNotSupported') }}
              </p>
            </object>
          </div>
          <v-alert v-else type="error" class="ma-4">
            {{ $t('tslConfigurator.previewError') }}
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Service from '@/services'
import Loader from '@/components/Loader'
import RichTextEditor from '@/components/forms/RichTextEditor'
import UploadFileButton from '@/components/forms/UploadFileButton'
import { fileUrl } from '@/utils/helpers'

export default {
  name: 'TSLConfigurator',
  components: {
    Loader,
    RichTextEditor,
    UploadFileButton
  },
  data () {
    return {
      loading: true,
      saving: false,
      valid: false,
      form: {
        logo: null,
        barcodeType: 0,
        bankAccountNumber: '',
        orderConditions: '',
        headerText: '',
        bankAccount2: '',
        swiftCode: '',
        swiftCode2: '',
        informationClause: ''
      },
      logoFile: null,
      logoPreview: null,
      barcodeTypes: [
        { value: 0, text: this.$t('tslConfigurator.barcodeOff') },
        { value: 1, text: this.$t('tslConfigurator.barcodeBarCode') },
        { value: 2, text: this.$t('tslConfigurator.barcodeQrCode') }
      ],
      previewDialog: false,
      loadingPreview: false,
      previewPdfUrl: null
    }
  },
  async mounted () {
    await this.loadConfiguration()
  },
  methods: {
    getLogoUrl (url) {
      if (this.logoPreview) {
        return this.logoPreview
      }
      return fileUrl(url)
    },
    async loadConfiguration () {
      try {
        this.loading = true
        const response = await Service.get('TSL/GetConfiguration')

        if (response.data) {
          this.form.logo = response.data.logo
          this.form.barcodeType = response.data.barcodeType
          this.form.bankAccountNumber = response.data.bankAccountNumber || ''
          this.form.orderConditions = response.data.orderConditions || ''
          this.form.headerText = response.data.headerText || ''
          this.form.bankAccount2 = response.data.bankAccount2 || ''
          this.form.swiftCode = response.data.swiftCode || ''
          this.form.swiftCode2 = response.data.swiftCode2 || ''
          this.form.informationClause = response.data.informationClause || ''
        }
      } catch (error) {
        if (error.response && error.response.status !== 404) {
          await this.$store.dispatch('setErrorNotification', this.$t('tslConfigurator.loadError'))
        }
      } finally {
        this.loading = false
      }
    },
    uploadLogo (files) {
      if (!files || files.length === 0) {
        return
      }

      this.logoFile = files[0]

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        this.logoPreview = e.target.result
      }
      reader.readAsDataURL(files[0])
    },
    async save () {
      if (!this.$refs.form.validate()) {
        return
      }

      try {
        this.saving = true
        const formData = new FormData()

        if (this.logoFile) {
          formData.append('Logo', this.logoFile)
        }

        formData.append('BarcodeType', this.form.barcodeType)
        formData.append('BankAccountNumber', this.form.bankAccountNumber)
        formData.append('OrderConditions', this.form.orderConditions)

        // Additional fields
        if (this.form.headerText) {
          formData.append('HeaderText', this.form.headerText)
        }
        if (this.form.bankAccount2) {
          formData.append('BankAccount2', this.form.bankAccount2)
        }
        if (this.form.swiftCode) {
          formData.append('SwiftCode', this.form.swiftCode)
        }
        if (this.form.swiftCode2) {
          formData.append('SwiftCode2', this.form.swiftCode2)
        }
        if (this.form.informationClause) {
          formData.append('InformationClause', this.form.informationClause)
        }

        await Service.post('TSL/SaveConfiguration', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        await this.$store.dispatch('setSuccessNotification', this.$t('tslConfigurator.saveSuccess'))

        // Clear temporary logo data
        this.logoFile = null
        this.logoPreview = null

        // Reload configuration to get updated logo URL
        await this.loadConfiguration()
      } catch (error) {
        await this.$store.dispatch('setErrorNotification', this.$t('tslConfigurator.saveError'))
      } finally {
        this.saving = false
      }
    },
    async openPreview () {
      if (!this.$refs.form.validate()) {
        await this.$store.dispatch('setWarningNotification',
          this.$t('tslConfigurator.fixValidationErrors'))
        return
      }

      this.previewDialog = true
      await this.generatePreview()
    },
    async generatePreview () {
      this.loadingPreview = true
      try {
        const formData = new FormData()

        // Add logo - either new one or existing one
        if (this.logoFile) {
          formData.append('Logo', this.logoFile)
        } else if (this.form.logo) {
          // If there's existing logo but no new file, we need to send it
          // Backend will handle the existing logo from filename
          formData.append('ExistingLogo', this.form.logo)
        }

        formData.append('BarcodeType', this.form.barcodeType)
        formData.append('BankAccountNumber', this.form.bankAccountNumber || '')
        formData.append('OrderConditions', this.form.orderConditions || '')

        if (this.form.headerText) {
          formData.append('HeaderText', this.form.headerText)
        }
        if (this.form.bankAccount2) {
          formData.append('BankAccount2', this.form.bankAccount2)
        }
        if (this.form.swiftCode) {
          formData.append('SwiftCode', this.form.swiftCode)
        }
        if (this.form.swiftCode2) {
          formData.append('SwiftCode2', this.form.swiftCode2)
        }
        if (this.form.informationClause) {
          formData.append('InformationClause', this.form.informationClause)
        }

        const response = await Service.postFileBlob('TSL/PreviewConfigurationOrderPDF', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        // Cleanup old URL if exists
        if (this.previewPdfUrl) {
          window.URL.revokeObjectURL(this.previewPdfUrl)
        }

        this.previewPdfUrl = window.URL.createObjectURL(response.data)
      } catch (error) {
        await this.$store.dispatch('setErrorNotification',
          this.$t('tslConfigurator.previewError'))
        this.previewPdfUrl = null
      } finally {
        this.loadingPreview = false
      }
    }
  },
  beforeDestroy () {
    // Cleanup blob URL
    if (this.previewPdfUrl) {
      window.URL.revokeObjectURL(this.previewPdfUrl)
    }
  }
}
</script>

<style scoped>
label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
}

.avatar {
  width: 100px;
  height: 100px;
  border: solid thin var(--v-primary-base);
  background-color: var(--v-background-base);
}

.avatar-responsive {
  width: 100px;
  height: 100px;
}

.pdf-container {
  min-height: 600px;
}

.pdf-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
}
</style>
